import { computed, h, ref, watch } from "vue";
import { Checkbox } from "@eudonet/opal";
/**
 * Selection state + checkbox column for a paginated list that surfaces the OPAL
 * FloatActionBar (render it via `ListFloatActionBar`). Selection is page-scoped:
 * it resets whenever the visible row-id set changes (page, filter, search, view),
 * so the bar's count can never include rows the user is no longer looking at.
 */
export function useListRowSelection(options) {
    const { t, rows, getRowId } = options;
    const selectedIds = ref({});
    const selectedIdList = computed(() => Object.entries(selectedIds.value)
        .filter(([, ticked]) => ticked)
        .map(([id]) => id));
    const selectedCount = computed(() => selectedIdList.value.length);
    const allPagesSelected = ref(false);
    const totalAcrossAllPages = computed(() => options.totalCount?.());
    const effectiveCount = computed(() => allPagesSelected.value ? (totalAcrossAllPages.value ?? selectedCount.value) : selectedCount.value);
    function clearSelection() {
        selectedIds.value = {};
        allPagesSelected.value = false;
    }
    function toggleRow(id) {
        allPagesSelected.value = false; // any manual toggle exits "all" mode
        selectedIds.value = { ...selectedIds.value, [id]: !selectedIds.value[id] };
    }
    const pageAllSelected = computed(() => rows().length > 0 && rows().every((row) => selectedIds.value[getRowId(row)]));
    const pageSomeSelected = computed(() => rows().some((row) => selectedIds.value[getRowId(row)]) && !pageAllSelected.value);
    function togglePageAll() {
        allPagesSelected.value = false; // any manual toggle exits "all" mode
        if (pageAllSelected.value) {
            clearSelection();
        }
        else {
            selectedIds.value = Object.fromEntries(rows().map((row) => [getRowId(row), true]));
        }
    }
    function onSelectAllPages(checked) {
        if (checked) {
            selectedIds.value = Object.fromEntries(rows().map((row) => [getRowId(row), true]));
            allPagesSelected.value = true;
        }
        else {
            clearSelection();
        }
    }
    watch(() => rows().map(getRowId).join("|"), (next, prev) => {
        if (next !== prev)
            clearSelection();
    });
    const selectColumn = {
        id: "select",
        header: () => h(Checkbox, {
            modelValue: pageAllSelected.value,
            indeterminate: pageSomeSelected.value,
            "onUpdate:modelValue": () => togglePageAll(),
            ariaLabel: t("common.selectAll"),
        }),
        cell: ({ row }) => h(Checkbox, {
            modelValue: !!selectedIds.value[getRowId(row.original)],
            "onUpdate:modelValue": () => toggleRow(getRowId(row.original)),
            ariaLabel: t("common.selectRow"),
        }),
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        resizable: false,
        draggable: false,
        showPinIcon: false,
        customHeader: true,
    };
    return {
        selectColumn,
        selectedCount,
        selectedIds: selectedIdList,
        allPagesSelected,
        totalAcrossAllPages,
        effectiveCount,
        clearSelection,
        onSelectAllPages,
    };
}
